// English-language content for the /en/ SEO section (British-run garden
// maintenance near Coëx, Vendée). French remains the primary site. These pages
// target English-speaking holiday-home / second-home / campsite-and-mobile-home
// owners, retirees and residents. SAP-compliant, careful tax-credit wording.

// Focused local service area (~15 minutes from Coëx).
export const EN_AREA = [
  'Coëx', 'Landevieille / Lac du Jaunay', 'La Chapelle-Hermier', 'Saint-Julien-des-Landes',
  'Apremont', "L'Aiguillon-sur-Vie", 'Saint-Révérend', 'Maché', 'Commequiers', 'Givrand',
];

// Careful, honest tax-credit wording used across the English section.
export const EN_TAX_NOTE =
  'Some garden maintenance services may be eligible for the French 50% tax credit under the Service à la Personne (SAP) system, for eligible French tax residents. Please ask if you are unsure — this is general information, not personal tax advice.';

export const EN_EXCLUSIONS = [
  'Risky tree surgery, felling and large/high hedges',
  'Major landscaping, garden creation and design',
  'Construction, earthworks and heavy works',
  'House clearance and removals',
  'Plumbing, electrical work, heating and gas',
  'Roof cleaning and anything that needs working at height',
  'Prohibited chemical weed treatments',
];

// Price list (TTC = incl. tax). "≈" = indicative cost after the 50% credit for eligible clients.
export const EN_PRICES = [
  { label: 'Lawn mowing', ttc: '0.28 €/m² incl. tax', after: '≈ 0.14 €/m²' },
  { label: 'Strimming / rough clearing', ttc: '0.75 €/m² incl. tax', after: '≈ 0.375 €/m²' },
  { label: 'Hedge trimming (by height)', ttc: '5 € / 7.50 € / 12 € per m incl. tax', after: '≈ half' },
  { label: 'Hourly work, weeding & finishing', ttc: '45 €/h incl. tax', after: '≈ 22.50 €/h' },
  { label: 'Holiday-home check visit', ttc: '30 € incl. tax', after: '≈ 15 €' },
  { label: 'Green waste (per trip, where offered)', ttc: '15 €/trip incl. tax', after: '≈ 7.50 €' },
  { label: 'Pressure washing & small handyman (hourly)', ttc: '45 €/h incl. tax', after: '≈ 22.50 €/h' },
];

export const EN_PRICES_NOTE =
  'All prices are shown including tax (TTC). The figure after “≈” is an indicative cost if the 50% French tax credit applies to you. ' + EN_TAX_NOTE + ' Payment is by bank transfer or cheque.';

export const EN_SERVICE_CARDS = [
  { icon: 'Sprout', title: 'Garden tidy-ups', text: 'Getting an overgrown or neglected garden back under control and usable again.' },
  { icon: 'Scissors', title: 'Lawn mowing', text: 'Regular or one-off mowing with clean, strimmed edges along borders, paths and walls.' },
  { icon: 'TreeDeciduous', title: 'Hedge cutting', text: 'Neat small and medium hedges, sides and tops, with the clippings cleared away.' },
  { icon: 'Flower2', title: 'Weeding & clearance', text: 'Hand weeding of beds, borders and paths, and clearing tired corners.' },
  { icon: 'Droplets', title: 'Pressure washing', text: 'Patios, terraces, paths and decking cleaned up before the season or guests.' },
  { icon: 'Home', title: 'Holiday-home care', text: 'Pre-arrival preparation, regular visits and photo updates while you are away.' },
];

export const EN_WHY_CHOOSE = [
  { title: 'Clear communication in English', text: 'A British owner who answers in plain English, so nothing gets lost in translation when you describe what you need.' },
  { title: 'Properly French-registered', text: 'A registered French micro-business declared under the Service à la Personne (SAP) scheme — real invoices, fully above board.' },
  { title: 'Clear, written quotes', text: 'You get a simple quote with prices including tax. No surprises and no pressure.' },
  { title: 'Reliable and regular', text: 'The same person comes back each time, with steady visits you can plan around — ideal for second and holiday homes.' },
  { title: 'Photos after the work', text: 'Away from your French home? Ask for photos after each visit so you can see the garden is properly looked after.' },
  { title: 'Local knowledge near Coëx', text: 'Based in Coëx, with hands-on experience of sandy soils, sea wind and the rhythm of holiday-home gardens.' },
];

// Conversion checklist shown across the English section.
export const EN_WHAT_TO_SEND = [
  'Your address, or the campsite / holiday-park name and pitch',
  'A few photos of the garden or outdoor area',
  'Approximate size (lawn, hedge length, terrace area)',
  'What needs doing and roughly how often',
  'Access details (gate codes, keys, parking)',
  'Whether green-waste removal is needed',
  'For campsites/mobile-homes: whether outside gardeners are allowed / permission is confirmed',
];

// ---- Link registry (kebab English slugs) ----
const L = {
  home: { to: '/en/', label: 'English-speaking gardener near Coëx' },
  services: { to: '/en/garden-maintenance-vendee', label: 'Garden maintenance services' },
  holiday: { to: '/en/holiday-home-garden-care-vendee', label: 'Holiday home garden care' },
  campsite: { to: '/en/campsite-garden-maintenance-vendee', label: 'Campsite & mobile-home gardens' },
  tidy: { to: '/en/garden-tidy-up-coex', label: 'Garden tidy-up near Coëx' },
  mowing: { to: '/en/lawn-mowing-coex', label: 'Lawn mowing near Coëx' },
  hedge: { to: '/en/hedge-cutting-coex', label: 'Hedge cutting near Coëx' },
  pressure: { to: '/en/pressure-washing-coex', label: 'Pressure washing near Coëx' },
  weeding: { to: '/en/weeding-garden-clearance-coex', label: 'Weeding & garden clearance' },
  tax: { to: '/en/sap-garden-tax-credit-france', label: 'French 50% tax credit explained' },
  contact: { to: '/en/contact', label: 'Get a quote / contact' },
  areaCoex: { to: '/en/areas/coex', label: 'Gardener in Coëx' },
  areaLande: { to: '/en/areas/landevieille-lac-du-jaunay', label: 'Gardener near Landevieille & Lac du Jaunay' },
  areaChapelle: { to: '/en/areas/la-chapelle-hermier', label: 'Gardener near La Chapelle-Hermier' },
  areaStJulien: { to: '/en/areas/saint-julien-des-landes', label: 'Gardener near Saint-Julien-des-Landes' },
  areaApremont: { to: '/en/areas/apremont', label: 'Gardener near Apremont' },
};

// ---- Service / money pages (route: /en/<slug>) ----
export const EN_PAGES = {
  'garden-maintenance-vendee': {
    section: 'Services',
    metaTitle: 'Garden Maintenance Services near Coëx, Vendée | English-speaking',
    metaDescription:
      'Friendly, reliable garden maintenance near Coëx, Vendée: lawn mowing, hedge cutting, weeding, garden tidy-ups, pressure washing and holiday-home care. British-run, English-speaking, French-registered (SAP).',
    h1: 'Garden maintenance services near Coëx',
    heroTagline: 'Mowing, hedges, weeding, tidy-ups and pressure washing — kept simple',
    heroIntro:
      'Le Rosebif is a British-run, English-speaking garden maintenance service based in Coëx, Vendée. Here is an overview of what I can do for holiday homes, second homes and local gardens, with clear communication in English and proper French invoices.',
    chips: ['Service à la personne', 'English-speaking', 'Near Coëx', 'Free quote'],
    intro: [
      'Whether you live here all year or visit a holiday home, a garden in this part of Vendée needs steady, sensible upkeep — especially with the coastal wind and warm, dry summers. I focus on the regular jobs that keep a garden looking after itself.',
      'Everything is offered to private individuals under the French Service à la Personne (SAP) scheme.',
    ],
    whoForTitle: 'What I can help with',
    whoFor: [
      'Garden tidy-ups for overgrown or neglected gardens',
      'Lawn mowing — one-off or regular, with strimmed edges',
      'Hedge cutting (small and medium hedges)',
      'Weeding and garden clearance',
      'Pressure washing of terraces, paths and decking',
      'Holiday-home and second-home garden care, with photos after the work',
    ],
    includedTitle: 'How it works',
    included: [
      'You send a few details and photos of the garden',
      'I reply in plain English with a clear, written quote',
      'We agree a one-off visit or a regular schedule',
      'The same person comes back each time',
      'Green-waste removal per trip, where offered',
      'Photos after the visit on request',
    ],
    notIncludedTitle: 'What I don’t take on',
    notIncluded: EN_EXCLUSIONS,
    showPrices: true,
    faq: [
      { q: 'Do you speak English?', a: 'Yes — Le Rosebif is British-run and based in Coëx, so you can describe what you need in plain English. Quotes and invoices follow the standard French format.' },
      { q: 'Do you do one-off jobs or only regular visits?', a: 'Both. Some clients book a single tidy-up; others prefer regular mowing and upkeep through the season.' },
      { q: 'How do I pay?', a: 'By bank transfer or cheque, against a proper invoice. I don’t use CESU and don’t promise “Avance Immédiate”.' },
      { q: 'Is it eligible for the French tax credit?', a: EN_TAX_NOTE },
    ],
    relatedLinks: [L.holiday, L.campsite, L.tidy, L.mowing, L.hedge, L.weeding, L.pressure, L.contact],
    frEquivalent: '/services',
  },

  'holiday-home-garden-care-vendee': {
    section: 'Holiday Home Care',
    metaTitle: 'Holiday Home Garden Care in Vendée (near Coëx) | English-speaking',
    metaDescription:
      'Holiday-home and second-home garden care near Coëx, Vendée: garden tidy-up and grass cutting before you arrive, hedges, weeding, terrace cleaning and pressure washing, one-off or regular visits, before/after photos. English-speaking.',
    h1: 'Holiday home garden care in Vendée',
    heroTagline: 'Arrive to a tidy garden — and peace of mind in between',
    heroIntro:
      'If your home near Coëx sits empty for weeks at a time, the garden can quickly get away from you. Le Rosebif looks after second and holiday homes so you arrive to a clean, welcoming garden — with clear updates in English while you are away.',
    chips: ['Second & holiday homes', 'Garden ready before arrival', 'Before/after photos', 'One-off or regular'],
    intro: [
      'I plan visits around your stays. A few days before you arrive, I mow, tidy the edges, weed the paths and clear away leaves, so the garden looks cared for from the moment you walk in.',
      'Between visits I can keep an eye on things and send a photo or two so you know everything is in order — all in plain English. This is practical garden care, around Coëx and the nearby villages.',
    ],
    whoForTitle: 'Who it’s for',
    whoFor: [
      'Owners of second homes and holiday homes near Coëx',
      'Families who arrive for weekends or holidays and want the garden ready',
      'Owners letting their property who want a tidy garden between guests',
      'Anyone away for long stretches who wants to avoid a “jungle” on return',
    ],
    includedTitle: 'What’s included',
    included: [
      'Garden tidy-up and grass cutting before owners arrive',
      'Hedges, weeding and a general tidy-up',
      'Terrace cleaning and pressure washing where suitable',
      'Leaf and debris clearing on hard surfaces',
      'One-off visits or a regular schedule',
      'Before/after photos sent after each visit',
    ],
    notIncludedTitle: 'What’s not included',
    notIncluded: EN_EXCLUSIONS,
    showPrices: true,
    ctaNote: 'Send your dates, your address, photos, access details and what needs doing — I’ll reply with a clear quote.',
    faq: [
      { q: 'Can you get the garden ready before we arrive?', a: 'Yes. Send me your dates and I’ll schedule a visit shortly before, so the garden is mowed, tidy and welcoming when you arrive.' },
      { q: 'Will you let me know it’s been done?', a: 'I can send before/after photos after each visit, so even from abroad you can see the garden is properly looked after.' },
      { q: 'Do you also keep an eye on the house?', a: 'I offer a simple garden and property check (a visual look-over and a quick update). This is not full key-holding or security — just reassuring, regular eyes on your place.' },
      { q: 'Is it eligible for the French tax credit?', a: EN_TAX_NOTE },
    ],
    relatedLinks: [L.tidy, L.mowing, L.hedge, L.weeding, L.pressure, L.campsite, L.contact],
    frEquivalent: '/entretien-residence-secondaire',
  },

  'campsite-garden-maintenance-vendee': {
    section: 'Campsite Gardens',
    metaTitle: 'Campsite & Mobile-Home Garden Maintenance in Vendée | English-speaking',
    metaDescription:
      'Garden maintenance for mobile-homes and campsite pitches near Coëx, Vendée — where the campsite allows outside gardeners or you have permission. Pitch tidy-ups, grass cutting, pressure washing decking, end-of-season tidy-ups, photos after the job.',
    h1: 'Campsite and mobile-home garden maintenance in Vendée',
    heroTagline: 'Pitch tidy-ups and decking clean-ups — where it’s allowed',
    heroIntro:
      'If you own a mobile-home or holiday caravan on a site near Coëx, Le Rosebif can help keep your pitch and outdoor area tidy — provided the campsite allows outside gardeners, or you already have the owner’s permission.',
    chips: ['Mobile-homes', 'Pitch tidy-ups', 'Decking & terraces', 'Where permitted'],
    intro: [
      'Please note: many holiday parks and campsites have their own grounds team and rules. I only carry out work where the site allows outside gardeners, or where you have confirmed permission. It’s always worth checking with the site office first.',
      'Where it is allowed, I help English-speaking owners keep their plot presentable — for example on sites around Landevieille, Lac du Jaunay, Saint-Julien-des-Landes and nearby areas. This does not imply any official partnership with any campsite.',
    ],
    whoForTitle: 'Who it’s for',
    whoFor: [
      'English-speaking mobile-home and caravan owners on sites near Coëx',
      'Owners who can’t get over often and want the plot ready before arrival',
      'Owners who want an end-of-season tidy-up after the summer',
      'Anyone whose site permits outside gardeners or who has owner permission',
    ],
    includedTitle: 'What I can do (where permitted)',
    included: [
      'Mobile-home pitch tidy-up',
      'Grass cutting and strimmed edges',
      'Hedge trimming where allowed',
      'Weeding around decking and terraces',
      'Pressure washing decking, terraces and outdoor areas where suitable',
      'Preparing the plot before arrival and end-of-season tidy-ups',
      'Photos after the job',
    ],
    notIncludedTitle: 'Important limits',
    notIncluded: [
      'Work on sites that do not allow outside gardeners',
      'Anything without the owner’s / site’s permission',
      'Risky tree work or large/high hedges',
      'Heavy works, construction or anything at height',
      'No implied partnership with any campsite or holiday park',
    ],
    showPrices: true,
    ctaNote: 'Tell me the site name and pitch, what needs doing, and whether the site allows outside gardeners — I’ll reply with a clear quote.',
    faq: [
      { q: 'Can you work on any campsite?', a: 'No. Many sites have their own grounds team or rules. I only work where the site allows outside gardeners or where you have confirmed permission — please check with the site office first.' },
      { q: 'Do you have a partnership with the campsites you mention?', a: 'No. Towns and sites are mentioned only as examples of the local area I can reach. There is no official partnership implied.' },
      { q: 'Can you get the plot ready before we arrive?', a: 'Yes, where permitted — send me your dates and I’ll tidy the pitch and clean the decking before you get there, with photos afterwards.' },
      { q: 'How do I pay?', a: 'By bank transfer or cheque, against a proper invoice.' },
    ],
    relatedLinks: [L.tidy, L.mowing, L.pressure, L.areaLande, L.areaStJulien, L.contact],
    frEquivalent: null,
  },

  'garden-tidy-up-coex': {
    section: 'Garden Tidy-up',
    metaTitle: 'Garden Tidy-up near Coëx, Vendée | English-speaking Gardener',
    metaDescription:
      'One-off garden tidy-ups for overgrown and neglected gardens near Coëx, Vendée: long grass, weeds, small hedges, paths and terraces. Ideal before holidays, guests, selling or renting. English-speaking, French-registered.',
    h1: 'Garden tidy-up near Coëx',
    heroTagline: 'Get an overgrown garden usable again',
    heroIntro:
      'Come back to a garden that has run wild? Le Rosebif handles one-off tidy-ups near Coëx — long grass, weeds, small hedges, paths and terraces — to get the garden looking cared for and usable again.',
    chips: ['One-off tidy-up', 'Overgrown gardens', 'Before holidays or guests', 'Near Coëx'],
    intro: [
      'A garden that’s been left for a few months can feel overwhelming. I bring it back under control in a sensible way: cut down the long grass, clear the weeds, trim the small hedges and tidy the paths and terraces.',
      'It’s ideal before a holiday, before guests arrive, before selling or renting, or simply after being away. From there, you can keep it that way with the odd visit, or leave it as a one-off.',
    ],
    whoForTitle: 'Perfect for',
    whoFor: [
      'Overgrown grass and weeds after time away',
      'Small hedges that have got out of shape',
      'Paths, terraces and borders that need clearing',
      'Getting a garden ready before holidays, guests, selling or renting',
    ],
    includedTitle: 'What’s included',
    included: [
      'Cutting down long, overgrown grass',
      'Weeding beds, borders, paths and gravel',
      'Trimming small hedges back into shape',
      'Clearing paths and terraces',
      'A general tidy-up and clear-away',
      'Green-waste removal per trip, where offered',
    ],
    notIncludedTitle: 'What’s not included',
    notIncluded: EN_EXCLUSIONS,
    showPrices: true,
    faq: [
      { q: 'My garden is really overgrown — can you still help?', a: 'In most cases yes. For very heavy or large sites I’ll let you know honestly what’s realistic and how much waste is involved before starting.' },
      { q: 'Can you keep it tidy afterwards?', a: 'Yes — many people start with a one-off tidy-up and then book the occasional visit to keep on top of it.' },
      { q: 'Is it eligible for the French tax credit?', a: EN_TAX_NOTE },
    ],
    relatedLinks: [L.holiday, L.mowing, L.hedge, L.weeding, L.pressure, L.contact],
    frEquivalent: null,
  },

  'lawn-mowing-coex': {
    section: 'Lawn Mowing',
    metaTitle: 'Lawn Mowing near Coëx, Vendée | English-speaking Gardener',
    metaDescription:
      'Grass cutting near Coëx, Vendée — one-off or regular mowing, holiday-home lawns and campsite/mobile-home plots where authorised, with strimmed edges and practical seasonal advice. English-speaking, French-registered (SAP).',
    h1: 'Lawn mowing near Coëx',
    heroTagline: 'One-off or regular grass cutting, edges included',
    heroIntro:
      'Le Rosebif keeps lawns tidy near Coëx — one-off cuts, regular mowing through the season, holiday-home lawns and, where authorised, campsite or mobile-home plots, always with the edges strimmed.',
    chips: ['One-off or regular', 'Holiday-home lawns', 'Strimmed edges', 'Near Coëx'],
    intro: [
      'Whether you need a single cut to take the pressure off, or steady mowing through spring and summer, I keep the grass at a sensible height and strim the edges along borders, paths and walls for a clean finish.',
      'For holiday homes, I time the cut just before you arrive. For campsite or mobile-home plots, I can help where the site allows outside gardeners or you have permission.',
    ],
    whoForTitle: 'What I offer',
    whoFor: [
      'One-off mowing when the grass has got away from you',
      'Regular mowing through the growing season',
      'Holiday-home lawns timed before your arrival',
      'Campsite / mobile-home plots where authorised',
      'Strimming of edges and awkward corners',
    ],
    includedTitle: 'Practical seasonal advice',
    included: [
      'Spring: regular cuts as growth speeds up',
      'Summer: slightly higher cut to protect against drought and sea wind',
      'Autumn: a final tidy and leaf clearing',
      'Edges strimmed along borders, paths and walls',
      'Grass left tidy and cleared as agreed',
    ],
    notIncludedTitle: 'What’s not included',
    notIncluded: EN_EXCLUSIONS,
    showPrices: true,
    faq: [
      { q: 'Can you mow my holiday-home lawn before we arrive?', a: 'Yes — just send your dates and I’ll cut and tidy the lawn shortly before you get there.' },
      { q: 'Do you mow campsite or mobile-home plots?', a: 'Where the site allows outside gardeners or you have permission, yes. Please check with the site office first.' },
      { q: 'Is it eligible for the French tax credit?', a: EN_TAX_NOTE },
    ],
    relatedLinks: [L.tidy, L.holiday, L.hedge, L.campsite, L.contact],
    frEquivalent: null,
  },

  'hedge-cutting-coex': {
    section: 'Hedge Cutting',
    metaTitle: 'Hedge Cutting near Coëx, Vendée | English-speaking Gardener',
    metaDescription:
      'Hedge trimming near Coëx, Vendée — small and medium hedges, sides and tops, keeping entrances, paths and terraces tidy, before arrival or visitors. Green-waste removal depending on volume. English-speaking, French-registered.',
    h1: 'Hedge cutting near Coëx',
    heroTagline: 'Neat small and medium hedges, tops and sides',
    heroIntro:
      'Le Rosebif trims small and medium hedges near Coëx — keeping entrances, paths and terraces clear and tidy, ready before you arrive or before visitors.',
    chips: ['Small & medium hedges', 'Sides & tops', 'Before arrival', 'Near Coëx'],
    intro: [
      'A trimmed hedge makes a real difference to how a property looks and feels. I cut the sides and tops to a sensible shape and clear the clippings away, so entrances, paths and terraces stay usable.',
      'For very large, high or risky hedges that need working at height or specialist equipment, I’ll point you towards a suitable professional — I focus on the safe, manageable hedges that keep a garden tidy.',
    ],
    whoForTitle: 'What I do',
    whoFor: [
      'Small and medium garden hedges',
      'Sides and tops trimmed to a tidy shape',
      'Keeping entrances, paths and terraces clear',
      'Tidying before arrival or before visitors',
      'Clearing the clippings away',
    ],
    includedTitle: 'Green waste',
    included: [
      'Clippings tidied and cleared from the work area',
      'Green-waste removal per trip, depending on volume and trailer capacity',
      'You can also keep the clippings for composting if you prefer',
      'Honest advice on timing through the year',
    ],
    notIncludedTitle: 'What’s not included',
    notIncluded: EN_EXCLUSIONS,
    showPrices: true,
    faq: [
      { q: 'Do you cut large or very high hedges?', a: 'I focus on small and medium hedges that can be trimmed safely from the ground or a low step. For large, high or risky hedges I’ll suggest a specialist.' },
      { q: 'Do you take the clippings away?', a: 'I tidy and clear the work area, and can remove green waste per trip depending on the volume and trailer capacity.' },
      { q: 'Is it eligible for the French tax credit?', a: EN_TAX_NOTE },
    ],
    relatedLinks: [L.tidy, L.holiday, L.weeding, L.mowing, L.contact],
    frEquivalent: null,
  },

  'pressure-washing-coex': {
    section: 'Pressure Washing',
    metaTitle: 'Pressure Washing near Coëx, Vendée | Terraces, Paths & Decking',
    metaDescription:
      'Pressure washing near Coëx, Vendée — patios, terraces, paths, decking and outdoor areas around mobile homes, before holidays. Practical clean-ups of slippery, dirty surfaces. English-speaking, French-registered.',
    h1: 'Pressure washing near Coëx',
    heroTagline: 'Patios, terraces, paths and decking, freshened up',
    heroIntro:
      'Le Rosebif cleans up tired outdoor surfaces near Coëx — patios, terraces, paths, decking and areas around mobile homes — so they look presentable again before the season or before guests.',
    chips: ['Patios & terraces', 'Paths & decking', 'Around mobile-homes', 'Before holidays'],
    intro: [
      'Over the winter, terraces and paths near the coast pick up algae, moss and grime, and can get slippery. A practical pressure wash brings them back to a clean, safer state.',
      'I clean patios, terraces, paths, decking and outdoor areas — including around mobile homes where permitted — keeping it practical rather than promising miracles on very old or damaged surfaces.',
    ],
    whoForTitle: 'What I clean',
    whoFor: [
      'Patios and terraces',
      'Paths and steps',
      'Decking and outdoor seating areas',
      'Areas around mobile homes (where permitted)',
      'Slippery, dirty or mossy surfaces before holidays',
    ],
    includedTitle: 'How I keep it practical',
    included: [
      'A sensible clean of suitable hard surfaces',
      'Honest advice if a surface is too old or fragile',
      'Tidying up afterwards',
      'Can be combined with a garden tidy-up or holiday-home visit',
    ],
    notIncludedTitle: 'What’s not included',
    notIncluded: EN_EXCLUSIONS,
    showPrices: true,
    faq: [
      { q: 'Will it make an old terrace look brand new?', a: 'It will look much cleaner, but I keep expectations realistic on very old, stained or damaged surfaces — I’ll tell you honestly what to expect.' },
      { q: 'Can you clean around my mobile home?', a: 'Yes, where the site allows outside gardeners or you have permission.' },
      { q: 'Can you do it with a garden tidy-up?', a: 'Yes — it’s often combined with a tidy-up or a holiday-home visit.' },
    ],
    relatedLinks: [L.tidy, L.holiday, L.campsite, L.mowing, L.contact],
    frEquivalent: null,
  },

  'weeding-garden-clearance-coex': {
    section: 'Weeding & Clearance',
    metaTitle: 'Weeding & Garden Clearance near Coëx, Vendée | English-speaking',
    metaDescription:
      'Weeding and garden clearance near Coëx, Vendée — weeds, brambles, borders, paths and neglected corners, mostly by hand. Making holiday homes and second homes presentable again. English-speaking, French-registered (SAP).',
    h1: 'Weeding and garden clearance near Coëx',
    heroTagline: 'Weeds, brambles, borders and tired corners — cleared',
    heroIntro:
      'Le Rosebif clears weeds, brambles, borders, paths and neglected corners near Coëx, mostly by hand, to make a garden presentable again — ideal for holiday homes and second homes.',
    chips: ['Hand weeding', 'Brambles & borders', 'Paths & corners', 'Near Coëx'],
    intro: [
      'Weeds and brambles can quickly take over beds, borders, gravel and the edges of a garden — especially at a home that’s left for weeks at a time. I clear them in a practical way, mostly by hand, without prohibited chemical treatments.',
      'For holiday homes and second homes, this makes a real difference to how the garden looks the moment you arrive. There are practical limits depending on the size of the job and the volume of waste, which I’ll always be honest about.',
    ],
    whoForTitle: 'What I clear',
    whoFor: [
      'Weeds in beds, borders, gravel and paths',
      'Brambles and overgrown edges',
      'Neglected corners and tired borders',
      'Making holiday homes and second homes presentable again',
    ],
    includedTitle: 'Practical limits',
    included: [
      'Mostly hand weeding (no prohibited chemical treatments)',
      'Clearing garden edges and borders',
      'Honest advice on big jobs and waste volume',
      'Green-waste removal per trip, where offered',
    ],
    notIncludedTitle: 'What’s not included',
    notIncluded: EN_EXCLUSIONS,
    showPrices: true,
    faq: [
      { q: 'Do you use weedkiller?', a: 'No prohibited chemical treatments — weeding is mostly done by hand, which is the safe and legal approach for private gardens.' },
      { q: 'There are a lot of brambles — can you still help?', a: 'Usually yes. For very large or heavy areas I’ll let you know what’s realistic and how much waste is involved before starting.' },
      { q: 'Is it eligible for the French tax credit?', a: EN_TAX_NOTE },
    ],
    relatedLinks: [L.tidy, L.mowing, L.hedge, L.holiday, L.contact],
    frEquivalent: null,
  },

  'sap-garden-tax-credit-france': {
    section: 'Tax Credit Explained',
    metaTitle: 'French 50% Tax Credit for Gardening (SAP), Explained | Le Rosebif',
    metaDescription:
      'A plain-English explanation of France’s Service à la Personne (SAP) scheme and the 50% tax credit on gardening for eligible private clients — how it works, what may qualify, limits and exclusions. Please check your personal eligibility.',
    h1: 'The French 50% tax credit for gardening, explained',
    heroTagline: 'Service à la Personne and the 50% credit — in plain English',
    heroIntro:
      'France has a long-standing home-services scheme called Service à la Personne (SAP). For eligible private individuals, regular garden maintenance carried out by a declared SAP provider may qualify for a 50% income-tax credit on the labour. Here’s a simple overview.',
    chips: ['Service à la personne', 'Private clients', 'May be eligible', 'Main & second homes'],
    intro: [
      'This page is a plain-English summary to help you understand how it generally works. It is general information only and not personal tax advice — please check your own eligibility (see the disclaimer below).',
    ],
    sections: [
      {
        title: 'How it works, in short',
        body: [
          'Declared SAP providers of garden maintenance can offer their services to private individuals under the SAP scheme. The labour element of eligible garden work may then qualify for a 50% income-tax credit.',
          'In practice, your quote and invoice show the full price including tax (TTC). Any credit is claimed afterwards through your French income-tax return.',
        ],
      },
      {
        title: 'Main homes and second homes',
        body: [
          'Regular garden maintenance may be eligible for both a main home and a second home in France, within the scheme’s rules and annual limits.',
          'There are yearly ceilings on the amount of garden work that qualifies, set by the French tax authorities. Your accountant or the official guidance can confirm the current limit for your situation.',
        ],
      },
    ],
    includedTitle: 'What typically may qualify',
    included: [
      'Lawn mowing and edging',
      'Hedge trimming and light work',
      'Strimming and rough clearing',
      'Hand weeding and bed maintenance',
      'General garden tidy-ups',
    ],
    notIncludedTitle: 'What does not qualify',
    notIncluded: [
      'Supply of materials (mulch, plants, seed, soil) — distinct from the labour',
      'Major landscaping, garden creation and design',
      'Risky tree surgery and felling',
      'Construction, earthworks and heavy works',
      'Plumbing, electrical, heating and gas work',
    ],
    showPrices: false,
    disclaimer:
      'This is a simple general explanation only and not personal tax advice. Rules, ceilings and eligibility can change and depend on your individual circumstances. Please check your personal eligibility with the French tax authorities or your accountant before relying on the tax credit. Not all English-speaking holiday-home owners automatically qualify.',
    faq: [
      { q: 'Do all holiday-home owners get the credit?', a: 'No. Eligibility depends on your individual circumstances as a French tax resident and on the scheme’s rules. Please check your personal eligibility.' },
      { q: 'Does it work for a second home?', a: 'Regular garden maintenance may qualify for both main and second homes within the scheme’s rules and annual limits. Check your personal eligibility.' },
      { q: 'Do you offer “Avance Immédiate”?', a: 'I keep things simple: any credit is recovered through your French tax return. Payment to me is by bank transfer or cheque, and I don’t use CESU.' },
      { q: 'Is there a limit?', a: 'Yes — there are annual ceilings set by the French tax authorities. Your accountant or the official guidance can confirm the current figure.' },
    ],
    relatedLinks: [L.holiday, L.services, L.contact],
    frEquivalent: '/credit-impot-jardinage',
  },
};

export const EN_PAGE_SLUGS = Object.keys(EN_PAGES);

// ---- Area pages (route: /en/areas/<slug>) — unique, locally useful ----
export const EN_AREA_PAGES = {
  'coex': {
    section: 'Coëx',
    townName: 'Coëx',
    metaTitle: 'English-speaking Gardener in Coëx, Vendée | Le Rosebif',
    metaDescription:
      'A British, English-speaking gardener based right in Coëx, Vendée. Lawn mowing, hedge cutting, weeding, tidy-ups and holiday-home care for local residents and second-home owners. French-registered (SAP).',
    h1: 'English-speaking gardener in Coëx',
    heroTagline: 'Based right here in Coëx',
    heroIntro:
      'Le Rosebif is based in Coëx itself, so you have a local, English-speaking gardener on your doorstep — for regular upkeep, one-off tidy-ups and looking after second homes between visits.',
    chips: ['Based in Coëx', 'English-speaking', 'Service à la personne'],
    intro: [
      'Being based in Coëx means no travel-time guesswork and an easy, local relationship: I know the village, its lanes and the mix of permanent homes and holiday properties here.',
      'From the bocage gardens inland to the breezier, sandier plots, I keep things tidy with sensible, regular care — mowing, hedges, weeding and clear-ups.',
    ],
    includedTitle: 'What I do in Coëx',
    included: [
      'Lawn mowing — one-off or regular',
      'Hedge cutting (small and medium hedges)',
      'Weeding and garden clearance',
      'Garden tidy-ups for overgrown gardens',
      'Pressure washing of terraces and paths',
      'Holiday-home and second-home care with photos',
    ],
    faq: [
      { q: 'Are you really based in Coëx?', a: 'Yes — Le Rosebif is based in Coëx, so you have a genuinely local, English-speaking gardener nearby.' },
      { q: 'Do you take on regular contracts?', a: 'Yes — regular mowing and upkeep through the season, or one-off visits, whichever suits you.' },
    ],
    relatedLinks: [L.mowing, L.hedge, L.weeding, L.tidy, L.holiday, L.contact],
  },
  'landevieille-lac-du-jaunay': {
    section: 'Landevieille & Lac du Jaunay',
    townName: 'Landevieille and Lac du Jaunay',
    metaTitle: 'English-speaking Gardener near Landevieille & Lac du Jaunay | Le Rosebif',
    metaDescription:
      'British, English-speaking garden maintenance near Landevieille and Lac du Jaunay, Vendée — a holiday and campsite area near Coëx. Mowing, tidy-ups, decking pressure washing and holiday-home care where permitted.',
    h1: 'English-speaking gardener near Landevieille and Lac du Jaunay',
    heroTagline: 'Holiday-home and lakeside garden care, minutes from Coëx',
    heroIntro:
      'Just a short drive from Coëx, the Landevieille and Lac du Jaunay area is full of holiday homes and campsites. Le Rosebif helps English-speaking owners keep gardens and plots tidy — including campsite work where the site allows it.',
    chips: ['Near Coëx', 'Holiday & campsite area', 'English-speaking'],
    intro: [
      'The lakeside and its campsites make this a busy holiday spot, with many owners away for much of the year. That’s exactly where reliable, English-speaking garden help is useful: a tidy plot before you arrive and a quick clean-up after the season.',
      'For campsite or mobile-home plots, I only work where the site allows outside gardeners or you have permission — it’s always worth checking with the site office first.',
    ],
    includedTitle: 'What I do here',
    included: [
      'Grass cutting and strimmed edges',
      'Garden and pitch tidy-ups (where permitted on campsites)',
      'Hedge trimming and weeding',
      'Pressure washing decking and terraces',
      'Pre-arrival preparation and end-of-season tidy-ups',
      'Photos after the work',
    ],
    faq: [
      { q: 'Can you work on the campsites here?', a: 'Only where the site allows outside gardeners or you have confirmed permission. Please check with the site office first — there’s no partnership implied.' },
      { q: 'How far is it from your base?', a: 'It’s a short drive from Coëx, so it’s well within my usual local area.' },
    ],
    relatedLinks: [L.campsite, L.holiday, L.mowing, L.pressure, L.contact],
  },
  'la-chapelle-hermier': {
    section: 'La Chapelle-Hermier',
    townName: 'La Chapelle-Hermier',
    metaTitle: 'English-speaking Gardener near La Chapelle-Hermier | Le Rosebif',
    metaDescription:
      'British, English-speaking garden maintenance near La Chapelle-Hermier, Vendée — a quiet village close to Coëx and the Jaunay lake. Mowing, hedges, weeding, tidy-ups and holiday-home care. French-registered (SAP).',
    h1: 'English-speaking gardener near La Chapelle-Hermier',
    heroTagline: 'Quiet, countryside gardens near the Jaunay',
    heroIntro:
      'La Chapelle-Hermier is a peaceful village close to Coëx and the Jaunay lake, with larger countryside plots. Le Rosebif keeps these gardens tidy with regular mowing, hedges and clear-ups, in plain English.',
    chips: ['Near Coëx', 'Countryside plots', 'English-speaking'],
    intro: [
      'The gardens here tend to be larger and more rural, which means steady mowing and hedge work through the season really pays off. I keep things manageable so the garden doesn’t run away over a busy summer.',
      'It’s an easy hop from Coëx, so regular visits or a pre-arrival tidy for a second home are simple to arrange.',
    ],
    includedTitle: 'What I do here',
    included: [
      'Lawn mowing for larger countryside plots',
      'Hedge cutting (small and medium hedges)',
      'Weeding and garden clearance',
      'One-off tidy-ups for overgrown gardens',
      'Holiday-home and second-home care with photos',
    ],
    faq: [
      { q: 'Do you handle larger rural gardens?', a: 'Yes — regular mowing and hedge work keep larger plots under control. For very large sites I’ll give you an honest plan first.' },
      { q: 'How far is it from Coëx?', a: 'Just a few minutes’ drive — well within my local area.' },
    ],
    relatedLinks: [L.mowing, L.hedge, L.tidy, L.holiday, L.contact],
  },
  'saint-julien-des-landes': {
    section: 'Saint-Julien-des-Landes',
    townName: 'Saint-Julien-des-Landes',
    metaTitle: 'English-speaking Gardener near Saint-Julien-des-Landes | Le Rosebif',
    metaDescription:
      'British, English-speaking garden maintenance near Saint-Julien-des-Landes, Vendée — a village with campsites and holiday homes near Coëx. Mowing, tidy-ups, decking pressure washing and holiday-home care where permitted.',
    h1: 'English-speaking gardener near Saint-Julien-des-Landes',
    heroTagline: 'Holiday-home and campsite garden help nearby',
    heroIntro:
      'Saint-Julien-des-Landes, with its campsites and holiday homes near the Jaunay, is a short drive from Coëx. Le Rosebif helps English-speaking owners keep gardens and plots tidy — including campsite work where allowed.',
    chips: ['Near Coëx', 'Campsites & holiday homes', 'English-speaking'],
    intro: [
      'This village sees a lot of holiday traffic in summer, so a reliable English-speaking gardener for the off-season tidy and the pre-arrival cut makes life much easier for owners who live elsewhere.',
      'On campsite or mobile-home plots I only work where the site allows outside gardeners or you have permission — please check with the site office first.',
    ],
    includedTitle: 'What I do here',
    included: [
      'Grass cutting and strimmed edges',
      'Garden and pitch tidy-ups (where permitted)',
      'Hedge trimming and weeding',
      'Pressure washing decking and terraces',
      'Pre-arrival preparation and photos after the work',
    ],
    faq: [
      { q: 'Can you work on the campsites near here?', a: 'Only where the site allows outside gardeners or you have confirmed permission — please check with the site office first.' },
      { q: 'Is it close to your base?', a: 'Yes — a short drive from Coëx, within my usual local area.' },
    ],
    relatedLinks: [L.campsite, L.holiday, L.mowing, L.pressure, L.contact],
  },
  'apremont': {
    section: 'Apremont',
    townName: 'Apremont',
    metaTitle: 'English-speaking Gardener near Apremont, Vendée | Le Rosebif',
    metaDescription:
      'British, English-speaking garden maintenance near Apremont, Vendée — a pretty village with a château and lake, close to Coëx. Mowing, hedges, weeding, tidy-ups and holiday-home care. French-registered (SAP).',
    h1: 'English-speaking gardener near Apremont',
    heroTagline: 'Village and second-home gardens near the château',
    heroIntro:
      'Apremont, known for its château and lake, sits a short drive from Coëx. Le Rosebif keeps gardens here tidy for residents and second-home owners — mowing, hedges, weeding and clear-ups, all in plain English.',
    chips: ['Near Coëx', 'Village & second homes', 'English-speaking'],
    intro: [
      'With its mix of village homes and properties owned by people who aren’t always here, Apremont is a natural fit for reliable, regular garden care and the occasional bigger tidy-up.',
      'It’s an easy run from Coëx, so timing a cut before you arrive — or keeping things ticking over while you’re away — is straightforward.',
    ],
    includedTitle: 'What I do here',
    included: [
      'Lawn mowing — one-off or regular',
      'Hedge cutting (small and medium hedges)',
      'Weeding and garden clearance',
      'One-off tidy-ups for overgrown gardens',
      'Holiday-home and second-home care with photos',
    ],
    faq: [
      { q: 'Do you cover Apremont regularly?', a: 'Yes — it’s within my local area, so regular visits or a pre-arrival tidy are easy to arrange.' },
      { q: 'Can you look after a second home here?', a: 'Yes — I time visits around your stays and can send photos after each one.' },
    ],
    relatedLinks: [L.mowing, L.hedge, L.weeding, L.holiday, L.contact],
  },
};

export const EN_AREA_SLUGS = Object.keys(EN_AREA_PAGES);

// Default cross-links (used where a page has no relatedLinks).
export const EN_INTERNAL_LINKS = [
  L.holiday, L.campsite, L.tidy, L.mowing, L.hedge, L.pressure, L.weeding, L.tax,
];

// Header / footer navigation for the English section.
export const EN_NAV = [
  { name: 'Home', path: '/en/' },
  { name: 'Holiday Home Care', path: '/en/holiday-home-garden-care-vendee' },
  { name: 'Campsite Gardens', path: '/en/campsite-garden-maintenance-vendee' },
  { name: 'Services', path: '/en/garden-maintenance-vendee' },
  { name: 'Areas', path: '/en/areas' },
  { name: 'Contact', path: '/en/contact' },
];

export const EN_FOOTER_SERVICES = [L.tidy, L.mowing, L.hedge, L.weeding, L.pressure, L.holiday, L.campsite];
export const EN_FOOTER_AREAS = [L.areaCoex, L.areaLande, L.areaChapelle, L.areaStJulien, L.areaApremont];
